// src/components/modals/EditCategoryModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2 } from 'lucide-react'
import CategoryService from '../../services/categoryService'

const EditCategoryModal = ({ isOpen, onClose, onCategoryUpdated, category }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Populate form when category prop changes
  useEffect(() => {
    if (category && isOpen) {
      console.log('📝 Populating form with category data:', category)
      setFormData({
        name: category.name || '',
        description: category.description || '',
        isActive: category.isActive !== undefined ? category.isActive : true
      })
      setErrors({})
    }
  }, [category, isOpen])

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Handle status toggle
  const handleStatusToggle = () => {
    setFormData(prev => ({
      ...prev,
      isActive: !prev.isActive
    }))
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required'
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      console.log('📦 Updating category:', { id: category._id, formData })
      const response = await CategoryService.updateCategory(category._id, formData)
      
      if (response.success) {
        console.log('✅ Category updated successfully:', response)
        onCategoryUpdated({ ...category, ...formData })
        handleClose()
      } else {
        console.log('⚠️ Update failed with response:', response)
        setErrors({ general: response.message || 'Failed to update category' })
      }
    } catch (error) {
      console.error('❌ Error updating category:', error)
      
      // Enhanced error handling
      let errorMessage = 'Failed to update category. Please try again.'
      
      if (error.message.includes('Not found')) {
        errorMessage = 'Category not found. It may have been deleted.'
      } else if (error.message.includes('Unauthorized')) {
        errorMessage = 'You are not authorized to update this category. Please login again.'
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.message.includes('duplicate') || error.message.includes('already exists')) {
        errorMessage = 'A category with this name already exists.'
      }
      
      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    setErrors({})
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Edit Category</h2>
            <button 
              onClick={handleClose} 
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Category Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category name
              </label>
              <Input
                name="name"
                placeholder="Hot Beverages"
                value={formData.name}
                onChange={handleInputChange}
                error={errors.name}
                disabled={loading}
                className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                placeholder="All the hot beverages are related to this."
                value={formData.description}
                onChange={handleInputChange}
                disabled={loading}
                className="w-full min-h-[100px] p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">{errors.description}</p>
              )}
            </div>

            {/* Status Toggle */}
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <button
                type="button"
                onClick={handleStatusToggle}
                disabled={loading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-6">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-black hover:bg-gray-800 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Category'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditCategoryModal